import type { I18n } from "@lingui/core"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Badge, Card, Group, Loader, Stack, Text, Title } from "@mantine/core"
import { Link } from "@tanstack/react-router"
import { useMemo } from "react"
import type { IChronicleEntry } from "../../../lib/api"
import { formatDate } from "../../../lib/format-date"
import { VoxStatusPill } from "../../VoxStatusPill"
import timeline from "../../WorldEntityShell/timelineEntry.module.css"
import classes from "../styles.module.css"
import type { ISessionGroup, IViewProps } from "./interfaces"

/**
 * Dumb view for CampaignChronicleView — receives the four parallel data
 * loads (chronicle entries + characters + NPCs + locations) and renders
 * the timeline. The smart wrapper at ../index.tsx owns the React Query
 * calls; everything here is derived from props.
 */
export function CampaignChronicleViewView({
  seriesId,
  entries,
  characters,
  npcs,
  locations,
  isLoading,
}: IViewProps): React.JSX.Element {
  const { i18n } = useLingui()

  const characterNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const c of characters) m.set(c.id, c.name)
    return m
  }, [characters])

  const npcNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const n of npcs) m.set(n.id, n.name)
    return m
  }, [npcs])

  const locationNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const l of locations) m.set(l.id, l.name)
    return m
  }, [locations])

  // Names already covered by an FK chip — used to suppress duplicate
  // "unresolved" orange chips when the LLM-written text-array mention
  // matches a canonical/aliased name on the FK side.
  const resolvedNpcNameSet = useMemo(() => {
    const set = new Set<string>()
    for (const n of npcs) {
      set.add(n.name.toLowerCase())
      for (const a of n.aliases) set.add(a.toLowerCase())
    }
    return set
  }, [npcs])

  const resolvedLocationNameSet = useMemo(() => {
    const set = new Set<string>()
    for (const l of locations) {
      set.add(l.name.toLowerCase())
      for (const a of l.aliases) set.add(a.toLowerCase())
    }
    return set
  }, [locations])

  const groupedSessions: ISessionGroup[] = useMemo(() => {
    const byMeeting = new Map<string | null, IChronicleEntry[]>()
    for (const entry of entries) {
      const key = entry.meetingId
      const list = byMeeting.get(key) ?? []
      list.push(entry)
      byMeeting.set(key, list)
    }
    const groups: ISessionGroup[] = []
    for (const [meetingId, list] of byMeeting) {
      list.sort((a, b) => (a.createdAt ?? "").localeCompare(b.createdAt ?? ""))
      groups.push({
        meetingId,
        label: formatSessionLabel(i18n, meetingId, list),
        entries: list,
      })
    }
    groups.sort((a, b) => {
      const aT = a.entries[0]?.meetingStartTime ?? a.entries[0]?.createdAt ?? ""
      const bT = b.entries[0]?.meetingStartTime ?? b.entries[0]?.createdAt ?? ""
      return bT.localeCompare(aT)
    })
    return groups
  }, [entries, i18n])

  if (isLoading) {
    return (
      <Group justify="center" py="md">
        <Loader size="sm" />
      </Group>
    )
  }

  if (!groupedSessions.length) {
    return (
      <Card withBorder padding="lg">
        <Stack align="center" gap="xs">
          <Text c="dimmed" fz="sm">
            <Trans>The chronicle is empty so far.</Trans>
          </Text>
          <Text c="dimmed" fz="xs">
            <Trans>Accepted chronicle entries from each session show up here in chronological order.</Trans>
          </Text>
        </Stack>
      </Card>
    )
  }

  return (
    <div className={timeline.root}>
      <Group justify="space-between" align="flex-end">
        <Title order={4}>
          <Trans>Campaign chronicle</Trans>
        </Title>
        <Text fz="xs" c="dimmed">
          <Trans>Accepted events, newest sessions first.</Trans>
        </Text>
      </Group>

      {groupedSessions.map((group) => (
        <div key={group.meetingId ?? "manual"} className={classes.sessionGroup}>
          <Text className={`${classes.sessionLabel} lex-eyebrow`}>{group.label}</Text>
          {group.entries.map((entry) => (
            <Card key={entry.id} withBorder={false} className={timeline.entryRow} data-timeline="chronicle">
              <div className={classes.entryHeader}>
                <VoxStatusPill tone={entryTypeTone(entry.entryType)}>{entry.entryType.replace("_", " ")}</VoxStatusPill>
                {entry.boundImageUrl && (
                  <Badge color="highlight" size="sm" variant="filled">
                    <Trans>plaque</Trans>
                  </Badge>
                )}
                <Text className={timeline.entryTitle}>{entry.title}</Text>
              </div>
              {entry.boundImageUrl && (
                <img src={entry.boundImageUrl} alt={entry.title} className={timeline.plaqueImage} loading="lazy" />
              )}
              <Text className={timeline.entryBody}>{entry.body}</Text>
              {(entry.relatedCharacterIds.length > 0 ||
                entry.relatedNpcIds.length > 0 ||
                entry.relatedLocationIds.length > 0 ||
                entry.relatedNpcs.length > 0 ||
                entry.relatedLocations.length > 0) && (
                <div className={classes.chipRow}>
                  {entry.relatedCharacterIds.map((id) => {
                    const name = characterNameById.get(id)
                    if (!name) return null
                    return (
                      <Badge key={id} size="sm" variant="dot" color="info">
                        {name}
                      </Badge>
                    )
                  })}
                  {entry.relatedNpcIds.map((id) => {
                    const name = npcNameById.get(id)
                    if (!name) return null
                    return (
                      <Link
                        key={id}
                        to="/campaigns/$id"
                        params={{ id: seriesId }}
                        search={{ tab: "npcs", entityId: id }}
                        style={{ textDecoration: "none" }}
                      >
                        <Badge size="sm" variant="light" color="brand" style={{ cursor: "pointer" }}>
                          {name}
                        </Badge>
                      </Link>
                    )
                  })}
                  {entry.relatedLocationIds.map((id) => {
                    const name = locationNameById.get(id)
                    if (!name) return null
                    return (
                      <Link
                        key={id}
                        to="/campaigns/$id"
                        params={{ id: seriesId }}
                        search={{ tab: "locations", entityId: id }}
                        style={{ textDecoration: "none" }}
                      >
                        <Badge size="sm" variant="light" color="success" style={{ cursor: "pointer" }}>
                          {name}
                        </Badge>
                      </Link>
                    )
                  })}
                  {entry.relatedNpcs
                    .filter((npc) => !resolvedNpcNameSet.has(npc.toLowerCase()))
                    .map((npc) => (
                      <Link
                        key={npc}
                        to="/campaigns/$id"
                        params={{ id: seriesId }}
                        search={{ tab: "npcs", createName: npc }}
                        style={{ textDecoration: "none" }}
                      >
                        <Badge
                          size="sm"
                          variant="outline"
                          color="warning"
                          style={{ cursor: "pointer" }}
                          title={i18n._(msg`Unresolved NPC mention — click to add to roster`)}
                        >
                          {npc}
                        </Badge>
                      </Link>
                    ))}
                  {entry.relatedLocations
                    .filter((loc) => !resolvedLocationNameSet.has(loc.toLowerCase()))
                    .map((loc) => (
                      <Link
                        key={loc}
                        to="/campaigns/$id"
                        params={{ id: seriesId }}
                        search={{ tab: "locations", createName: loc }}
                        style={{ textDecoration: "none" }}
                      >
                        <Badge
                          size="sm"
                          variant="outline"
                          color="warning"
                          style={{ cursor: "pointer" }}
                          title={i18n._(msg`Unresolved location mention — click to add to roster`)}
                        >
                          {loc}
                        </Badge>
                      </Link>
                    ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      ))}
    </div>
  )
}

function entryTypeTone(
  entryType: IChronicleEntry["entryType"],
): "active" | "tension" | "reward" | "revealed" | "neutral" {
  switch (entryType) {
    case "discovery":
    case "introduction":
      return "revealed"
    case "world_change":
      return "tension"
    case "relationship_change":
      return "reward"
    case "decision":
      return "active"
    default:
      return "neutral"
  }
}

function formatSessionLabel(i18n: I18n, meetingId: string | null, group: IChronicleEntry[]): string {
  if (!meetingId) return "Manual entries"
  const subject = group[0]?.meetingSubject?.trim()
  const dateStr = formatDate(i18n, group[0]?.meetingStartTime ?? group[0]?.createdAt ?? null)
  if (subject && dateStr) return `${subject} — ${dateStr}`
  if (subject) return subject
  if (dateStr) return dateStr
  return `Session ${meetingId.slice(0, 8)}`
}
